import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { addressOneLine, shippingDestination } from "@/lib/shippingDestination";
import { Copy, FileText, Flag, LayoutGrid, List, Loader2, RefreshCw } from "lucide-react";
import { PrintPacketDialog } from "@/components/tracking/PrintPacketDialog";
import { cn } from "@/lib/utils";
import { fillBlockTokens, useContentBlocks } from "@/lib/contentBlocks";
import { deriveLegalName } from "@/lib/legalNameRule";
import { INTERVIEW_FIELDS, addressOne, formationDefaults, readAppSettings, type SettingsMap } from "@/lib/formationDefaults";

const STAGES = [
  "ready",
  "ein_acquired",
  "filings_submitted",
  "filings_received",
  "docs_sent",
  "docs_delivered",
] as const;

type Stage = (typeof STAGES)[number];

const STAGE_LABEL: Record<string, string> = {
  pending: "Not started",
  ready: "Ready",
  ein_acquired: "EIN in hand",
  filings_submitted: "Filed with the state",
  filings_received: "State has processed",
  docs_sent: "Documents shipped",
  docs_delivered: "Documents delivered",
};

/** How many days in a stage is normal before we want eyes on it. */
const STAGE_THRESHOLD_DAYS: Record<string, number> = {
  pending: 3,
  ready: 3,
  ein_acquired: 3,
  filings_submitted: 21,
  filings_received: 3,
  docs_sent: 7,
  docs_delivered: 14,
};

type MemberRow = {
  id: string;
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  una_name: string | null;
  suite_number: number | null;
  fulfillment_stage: string;
  tracking_number: string | null;
  qr_shortcode: string | null;
  updated_at: string;
  legal_address?: unknown;
  shipping_line1?: string | null;
  shipping_line2?: string | null;
  shipping_city?: string | null;
  shipping_state?: string | null;
  shipping_zip?: string | null;
  phone?: string | null;
  legal_full_name?: string | null;
  ca_address?: unknown;
  mea_signed_at?: string | null;
};

type EventRow = {
  member_record_id: string;
  to_stage: string;
  created_at: string;
};

type CorrectionRow = {
  id: string;
  member_record_id: string;
  field_key: string;
  current_value: string | null;
  requested_value: string | null;
  member_note: string | null;
  status: string;
  created_at: string;
};

function memberName(m: MemberRow) {
  const name = [m.first_name, m.last_name].filter(Boolean).join(" ").trim();
  return name || m.email || "Member";
}

function daysSince(iso: string | null) {
  if (!iso) return 0;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
}

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/** The EIN worksheet, in the order the IRS screens ask for it. */
function worksheetRows(
  member: MemberRow,
  formation: any,
  settings: Record<string, string>,
  responsibleParty: string,
): [string, string][] {
  const f = formation ?? {};
  const interview = (f.interview ?? {}) as Record<string, string>;
  const legalName = f.legal_name || deriveLegalName(member.una_name || "");
  const tradeName = (member.una_name || "").trim() === legalName.trim() ? "" : (member.una_name || "");

  const startSource = interview.OrgDate || (member.mea_signed_at ? String(member.mea_signed_at).slice(0, 10) : "");
  const startMonth = interview.start_month
    || (startSource ? MONTHS[Number(startSource.slice(5, 7)) - 1] ?? "" : "");
  const startYear = interview.start_year || (startSource ? startSource.slice(0, 4) : "");

  const physical = addressOne(member.legal_address) || addressOneLine(shippingDestination(member).address);

  return [
    ["Organization type", f.org_type || settings.ein_default_org_type || ""],
    ["Reason for applying", f.reason || "Started a new business"],
    ["Role", f.ein_role || "Officer of the organization"],
    ["Legal name", legalName],
    ["Trade name", tradeName],
    ["County", f.county || ""],
    ["State", f.state || "California"],
    ["Start month", startMonth],
    ["Start year", startYear],
    ["Physical address", physical],
    ["Phone", f.phone || member.phone || ""],
    ["Care of name", f.care_of_name || ""],
    ["Separate mail address", "No"],
    ["Heavy vehicle", "No"],
    ["Gambling", "No"],
    ["Form 720", "No"],
    ["Alcohol tobacco firearms", "No"],
    ["W-2 employees", "No"],
    ["Activity category", f.activity_category || settings.ein_default_activity_category || ""],
    ["Activity subtype", f.activity_subtype || settings.ein_default_activity_subtype || ""],
    ["Activity text", f.activity_text || settings.ein_default_activity_text || ""],
    ["NAICS (our note, not an IRS field)", f.naics || ""],
    ["Responsible party", responsibleParty],
  ];
}

export default function Fulfillment() {
  const [view, setView] = useState<"table" | "board">("table");
  const [rows, setRows] = useState<MemberRow[]>([]);
  const [enteredAt, setEnteredAt] = useState<Record<string, string>>({});
  const [corrections, setCorrections] = useState<CorrectionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [stageFilter, setStageFilter] = useState<string>("all");
  const [sortKey, setSortKey] = useState<"days" | "name" | "stage">("days");

  const [target, setTarget] = useState<{ member: MemberRow; toStage: Stage } | null>(null);
  const [note, setNote] = useState("");
  const [tracking, setTracking] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [saving, setSaving] = useState(false);
  const [mintedUrl, setMintedUrl] = useState<string | null>(null);

  // The wording of every member email lives in the content editor.
  const { blocks: emailBlocks } = useContentBlocks("email");

  // Formation rows carry the EIN worksheet and the correction flag.
  const [formations, setFormations] = useState<Record<string, any>>({});
  const [worksheetFor, setWorksheetFor] = useState<MemberRow | null>(null);
  const [einValue, setEinValue] = useState("");
  const [founders, setFounders] = useState<{ id: string; display_name: string }[]>([]);
  const [founderId, setFounderId] = useState("");
  const [flagFor, setFlagFor] = useState<MemberRow | null>(null);
  const [flagReason, setFlagReason] = useState("");
  const [flagReturned, setFlagReturned] = useState("");
  const [flagResubmitted, setFlagResubmitted] = useState("");
  const [settings, setSettings] = useState<SettingsMap>({});
  const [interviewFor, setInterviewFor] = useState<MemberRow | null>(null);
  const [interviewDraft, setInterviewDraft] = useState<Record<string, string>>({});
  const [packetFor, setPacketFor] = useState<MemberRow | null>(null);



  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await (supabase as any)
      .from("member_records")
      .select("id, user_id, first_name, last_name, email, una_name, suite_number, fulfillment_stage, tracking_number, qr_shortcode, updated_at, legal_address, phone, legal_full_name, ca_address, mea_signed_at, shipping_line1, shipping_line2, shipping_city, shipping_state, shipping_zip")
      .neq("fulfillment_stage", "pending")
      .order("updated_at", { ascending: false })
      .limit(1000);

    if (error) {
      toast.error(error.message);
      setLoading(false);
      return;
    }
    const list = (data || []) as MemberRow[];
    setRows(list);

    const { data: events } = await (supabase as any)
      .from("fulfillment_events")
      .select("member_record_id, to_stage, created_at")
      .order("created_at", { ascending: true })
      .limit(4000);

    const entered: Record<string, string> = {};
    for (const e of (events || []) as EventRow[]) {
      entered[`${e.member_record_id}:${e.to_stage}`] = e.created_at;
    }
    setEnteredAt(entered);

    const { data: fixes } = await (supabase as any)
      .from("record_correction_requests")
      .select("*")
      .eq("status", "open")
      .order("created_at", { ascending: true })
      .limit(200);
    setCorrections((fixes || []) as CorrectionRow[]);

    const [{ data: forms }, { data: founderRows }] = await Promise.all([
      (supabase as any).from("formations").select("*").limit(1000),
      (supabase as any).from("platform_founders").select("id, display_name").eq("active", true),
    ]);
    const byMember: Record<string, any> = {};
    for (const f of forms || []) byMember[f.member_record_id] = f;
    setFormations(byMember);
    setFounders((founderRows || []) as { id: string; display_name: string }[]);
    setSettings(await readAppSettings());

    setLoading(false);
  }, []);

  /** Every member in formation gets a formation row the first time we open one. */
  const ensureFormation = useCallback(
    async (member: MemberRow) => {
      const existing = formations[member.id];
      if (existing) return existing;
      const { data, error } = await (supabase as any)
        .from("formations")
        .insert(formationDefaults(member, settings))
        .select("*")
        .single();
      if (error) {
        toast.error(error.message);
        return null;
      }
      setFormations((prev) => ({ ...prev, [member.id]: data }));
      return data;
    },
    [formations, settings],
  );

  /** Saves the founders interview for one member. */
  const saveInterview = async (member: MemberRow) => {
    const formation = await ensureFormation(member);
    if (!formation) return;
    setSaving(true);
    const { error } = await (supabase as any)
      .from("formations")
      .update({ interview: interviewDraft })
      .eq("id", formation.id);
    setSaving(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Interview saved");
    setInterviewFor(null);
    void load();
  };

  useEffect(() => {
    void load();
  }, [load]);

  const stageDays = useCallback(
    (m: MemberRow) => daysSince(enteredAt[`${m.id}:${m.fulfillment_stage}`] ?? m.updated_at),
    [enteredAt],
  );

  const visible = useMemo(() => {
    const list = rows.filter((r) => stageFilter === "all" || r.fulfillment_stage === stageFilter);
    const sorted = [...list];
    if (sortKey === "days") sorted.sort((a, b) => stageDays(b) - stageDays(a));
    if (sortKey === "name") sorted.sort((a, b) => memberName(a).localeCompare(memberName(b)));
    if (sortKey === "stage") {
      sorted.sort((a, b) => STAGES.indexOf(a.fulfillment_stage as Stage) - STAGES.indexOf(b.fulfillment_stage as Stage));
    }
    return sorted;
  }, [rows, stageFilter, sortKey, stageDays]);

  const openMove = (member: MemberRow, toStage: Stage) => {
    setTarget({ member, toStage });
    setNote("");
    setTracking(member.tracking_number || "");
    setConfirmText("");
    setMintedUrl(null);
  };

  const nextStage = (member: MemberRow): Stage | null => {
    const i = STAGES.indexOf(member.fulfillment_stage as Stage);
    if (member.fulfillment_stage === "pending") return "ready";
    if (i < 0 || i >= STAGES.length - 1) return null;
    return STAGES[i + 1];
  };

  const movingBackward = target
    ? STAGES.indexOf(target.toStage) < STAGES.indexOf(target.member.fulfillment_stage as Stage)
    : false;

  /** The exact email this move will send, so nobody sends a surprise. */
  const emailPreview = useMemo(() => {
    if (!target || movingBackward) return null;
    const block = emailBlocks[`milestone_${target.toStage}`];
    if (!block) return null;
    const tokens = {
      first_name: target.member.first_name ?? "",
      una_name: target.member.una_name ?? "your Association",
      tracking_number: tracking || target.member.tracking_number || "",
      portal_url: "https://wlthystwrd.com",
      note,
    };
    return {
      subject: fillBlockTokens(block.subject ?? "", tokens),
      body: fillBlockTokens(block.body_markdown ?? "", tokens),
    };
  }, [target, movingBackward, emailBlocks, tracking, note]);

  /** Records the EIN, then opens the move so the member gets the news. */
  const recordEin = async (member: MemberRow) => {
    const clean = einValue.trim();
    if (!/^\d{2}-?\d{7}$/.test(clean)) {
      toast.error("An EIN is nine digits, written as 12-3456789.");
      return;
    }
    const formation = await ensureFormation(member);
    if (!formation) return;
    setSaving(true);
    const stamp = new Date().toISOString().slice(0, 10);
    const [{ error: recError }, { error: formError }] = await Promise.all([
      (supabase as any).from("member_records").update({ ein: clean }).eq("id", member.id),
      (supabase as any)
        .from("formations")
        .update({
          ein_acquired_on: stamp,
          ein_responsible_founder_id: founderId || formation.ein_responsible_founder_id || null,
        })
        .eq("id", formation.id),
    ]);
    setSaving(false);
    if (recError || formError) {
      toast.error((recError || formError)?.message ?? "That did not save.");
      return;
    }
    toast.success("EIN recorded");
    setWorksheetFor(null);
    setEinValue("");
    await load();
    openMove(member, "ein_acquired");
  };

  /** The correction flag is a note for the team. It never moves a card and never emails. */
  const saveCorrection = async (member: MemberRow) => {
    const formation = await ensureFormation(member);
    if (!formation) return;
    setSaving(true);
    const { error } = await (supabase as any)
      .from("formations")
      .update({
        ca_returned_on: flagReturned || null,
        ca_return_reason: flagReason || null,
        ca_resubmitted_on: flagResubmitted || null,
        ca_correction_open: Boolean(flagReturned) && !flagResubmitted,
      })
      .eq("id", formation.id);
    setSaving(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Noted. The member sees nothing and the stage did not change.");
    setFlagFor(null);
    void load();
  };


  const submitMove = async () => {
    if (!target) return;
    if (movingBackward && confirmText.trim().toUpperCase() !== "MOVE BACK") {
      toast.error("Type MOVE BACK to confirm this one.");
      return;
    }
    setSaving(true);
    const { data, error } = await supabase.functions.invoke("fulfillment-advance", {
      body: {
        member_record_id: target.member.id,
        to_stage: target.toStage,
        note,
        tracking_number: tracking,
      },
    });
    setSaving(false);

    const failure = error?.message || (data as any)?.error;
    if (failure) {
      toast.error(String(failure));
      return;
    }
    toast.success(`Moved to ${STAGE_LABEL[target.toStage]}`);
    const url = (data as any)?.qr_url as string | null;
    if (target.toStage === "docs_sent" && url) {
      setMintedUrl(url);
    } else {
      setTarget(null);
    }
    void load();
  };

  const resolveCorrection = async (row: CorrectionRow) => {
    const { error } = await (supabase as any)
      .from("record_correction_requests")
      .update({ status: "resolved", resolved_at: new Date().toISOString() })
      .eq("id", row.id);
    if (error) {
      toast.error(error.message);
      return;
    }
    setCorrections((prev) => prev.filter((c) => c.id !== row.id));
    toast.success("Marked handled");
  };

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Fulfillment</h1>
          <p className="text-muted-foreground text-sm">
            Every member in formation, and where they stand today.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant={view === "table" ? "default" : "outline"} size="sm" onClick={() => setView("table")}>
            <List className="h-4 w-4 mr-2" /> Table
          </Button>
          <Button variant={view === "board" ? "default" : "outline"} size="sm" onClick={() => setView("board")}>
            <LayoutGrid className="h-4 w-4 mr-2" /> Board
          </Button>
          <Button variant="ghost" size="sm" onClick={() => void load()}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading
        </div>
      ) : view === "table" ? (
        <Card>
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
            <CardTitle className="text-base">{visible.length} members</CardTitle>
            <div className="flex items-center gap-2">
              <Select value={stageFilter} onValueChange={setStageFilter}>
                <SelectTrigger className="w-52"><SelectValue placeholder="All stages" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All stages</SelectItem>
                  {STAGES.map((s) => (
                    <SelectItem key={s} value={s}>{STAGE_LABEL[s]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={sortKey} onValueChange={(v) => setSortKey(v as typeof sortKey)}>
                <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="days">Longest waiting</SelectItem>
                  <SelectItem value="name">Member name</SelectItem>
                  <SelectItem value="stage">Stage order</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Member</TableHead>
                  <TableHead>Association</TableHead>
                  <TableHead>Stage</TableHead>
                  <TableHead>Days here</TableHead>
                  <TableHead>Suite</TableHead>
                  <TableHead>Last updated</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visible.map((m) => {
                  const days = stageDays(m);
                  const over = days > (STAGE_THRESHOLD_DAYS[m.fulfillment_stage] ?? 7);
                  const next = nextStage(m);
                  return (
                    <TableRow key={m.id}>
                      <TableCell>
                        <Link className="font-medium hover:underline" to={`/admin/members/${m.id}`}>
                          {memberName(m)}
                        </Link>
                        <div className="text-xs text-muted-foreground">{m.email}</div>
                      </TableCell>
                      <TableCell>{m.una_name || "Not named yet"}</TableCell>
                      <TableCell><Badge variant="secondary">{STAGE_LABEL[m.fulfillment_stage]}</Badge></TableCell>
                      <TableCell className={cn(over && "text-destructive font-semibold")}>{days}</TableCell>
                      <TableCell>{m.suite_number ?? ""}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {new Date(m.updated_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        {next ? (
                          <Button size="sm" variant="outline" onClick={() => openMove(m, next)}>
                            Move to {STAGE_LABEL[next]}
                          </Button>
                        ) : (
                          <span className="text-xs text-muted-foreground">Complete</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-6">
          {STAGES.map((stage) => {
            const cards = rows.filter((r) => r.fulfillment_stage === stage);
            return (
              <div
                key={stage}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const id = e.dataTransfer.getData("text/plain");
                  const member = rows.find((r) => r.id === id);
                  if (!member || member.fulfillment_stage === stage) return;
                  const from = member.fulfillment_stage === "pending" ? -1 : STAGES.indexOf(member.fulfillment_stage as Stage);
                  const to = STAGES.indexOf(stage);
                  // One step at a time going forward, so every member email really fires.
                  if (to > from && to - from > 1) {
                    toast.error("Move one column at a time. Each step sends the member a note.");
                    return;
                  }
                  openMove(member, stage);
                }}

                className="rounded-lg border bg-muted/30 p-2 min-h-[220px]"
              >
                <div className="px-1 pb-2 text-sm font-semibold">
                  {STAGE_LABEL[stage]}
                  <span className="ml-2 text-xs text-muted-foreground">{cards.length}</span>
                </div>
                <div className="space-y-2">
                  {cards.map((m) => {
                    const days = stageDays(m);
                    const over = days > (STAGE_THRESHOLD_DAYS[stage] ?? 7);
                    return (
                      <div
                        key={m.id}
                        draggable
                        onDragStart={(e) => e.dataTransfer.setData("text/plain", m.id)}
                        className="rounded-md border bg-background p-2 text-sm shadow-sm cursor-grab active:cursor-grabbing"
                      >
                        <Link className="font-medium hover:underline" to={`/admin/members/${m.id}`}>
                          {memberName(m)}
                        </Link>
                        <div className="text-xs text-muted-foreground">{m.una_name || "Not named yet"}</div>
                        {addressOneLine(shippingDestination(m).address) && (
                          <div className="text-xs text-muted-foreground mt-1">
                            Ships to: {addressOneLine(shippingDestination(m).address)}
                            {shippingDestination(m).isSeparate ? " (separate shipping address)" : ""}
                          </div>
                        )}
                        <div className={cn("text-xs mt-1", over ? "text-destructive font-semibold" : "text-muted-foreground")}>
                          {days} days here
                        </div>
                        {m.fulfillment_stage === "ready" && !formations[m.id]?.ein_scheduled_for && (
                          <div className="mt-1 text-xs font-medium text-amber-600">Needs an EIN day</div>
                        )}
                        {formations[m.id]?.ein_scheduled_for && !formations[m.id]?.ein_acquired_on && (
                          <div className="mt-1 text-xs text-muted-foreground">
                            EIN day: {formations[m.id].ein_scheduled_for}
                          </div>
                        )}
                        {formations[m.id]?.ca_correction_open && (
                          <div className="mt-1 text-xs font-medium text-amber-600">
                            State sent this back. Correction open.
                          </div>
                        )}
                        <div className="mt-2 flex flex-wrap gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 px-2 text-xs"
                            onClick={() => {
                              setWorksheetFor(m);
                              setEinValue("");
                              setFounderId(formations[m.id]?.ein_responsible_founder_id ?? "");
                              void ensureFormation(m);
                            }}
                          >
                            <FileText className="mr-1 h-3 w-3" /> EIN worksheet
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 px-2 text-xs"
                            onClick={() => {
                              setFlagFor(m);
                              setFlagReason(formations[m.id]?.ca_return_reason ?? "");
                              setFlagReturned(formations[m.id]?.ca_returned_on ?? "");
                              setFlagResubmitted(formations[m.id]?.ca_resubmitted_on ?? "");
                              void ensureFormation(m);
                            }}
                          >
                            <Flag className="mr-1 h-3 w-3" /> Flag
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 px-2 text-xs"
                            onClick={() => {
                              setInterviewFor(m);
                              setInterviewDraft({
                                ...(formationDefaults(m, settings).interview as Record<string, string>),
                                ...((formations[m.id]?.interview ?? {}) as Record<string, string>),
                              });
                              void ensureFormation(m);
                            }}
                          >
                            Interview
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 px-2 text-xs"
                            onClick={() => {
                              setPacketFor(m);
                              void ensureFormation(m);
                            }}
                          >
                            <FileText className="mr-1 h-3 w-3" /> Packet
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* The EIN worksheet reads in the order the IRS screens ask for it. */}
      <Dialog open={Boolean(worksheetFor)} onOpenChange={(o) => !o && setWorksheetFor(null)}>
        <DialogContent className="max-w-lg max-h-[85dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>EIN worksheet</DialogTitle>
            <DialogDescription>
              {worksheetFor ? memberName(worksheetFor) : ""}. Copy each value into the IRS screens in this order.
            </DialogDescription>
          </DialogHeader>
          {worksheetFor && (
            <div className="space-y-2">
              {worksheetRows(
                worksheetFor,
                formations[worksheetFor.id],
                settings,
                founders.find(
                  (f) => f.id === (formations[worksheetFor.id]?.ein_responsible_founder_id ?? founderId),
                )?.display_name || "",
              ).map(([label, value]) => (
                <div key={String(label)} className="flex items-center justify-between gap-2 rounded-md border px-3 py-2">
                  <div className="min-w-0">
                    <div className="text-xs text-muted-foreground">{label}</div>
                    <div className="truncate text-sm">{String(value || "Not set")}</div>
                  </div>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 shrink-0"
                    onClick={() => {
                      void navigator.clipboard.writeText(String(value ?? ""));
                      toast.success("Copied");
                    }}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              ))}

              <div className="space-y-2 rounded-md border p-3">
                <Label>Founder who applied</Label>
                <Select value={founderId} onValueChange={setFounderId}>
                  <SelectTrigger><SelectValue placeholder="Pick a founder" /></SelectTrigger>
                  <SelectContent>
                    {founders.map((f) => (
                      <SelectItem key={f.id} value={f.id}>{f.display_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Label>EIN</Label>
                <Input value={einValue} onChange={(e) => setEinValue(e.target.value)} placeholder="12-3456789" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setWorksheetFor(null)}>Close</Button>
            <Button disabled={saving} onClick={() => worksheetFor && recordEin(worksheetFor)}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Record EIN
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* The founders interview. Plain labels, nothing technical on screen. */}
      <Dialog open={Boolean(interviewFor)} onOpenChange={(o) => !o && setInterviewFor(null)}>
        <DialogContent className="max-w-lg max-h-[85dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Founders interview</DialogTitle>
            <DialogDescription>
              {interviewFor ? memberName(interviewFor) : ""}. These answers fill the founding papers.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {INTERVIEW_FIELDS.map((field) => (
              <div key={field.key} className="space-y-1">
                <Label>{field.label}</Label>
                <Input
                  type={field.type === "date" ? "date" : "text"}
                  value={interviewDraft[field.key] ?? ""}
                  onChange={(e) =>
                    setInterviewDraft((prev) => ({ ...prev, [field.key]: e.target.value }))
                  }
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInterviewFor(null)}>Close</Button>
            <Button disabled={saving} onClick={() => interviewFor && saveInterview(interviewFor)}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save interview
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Correction flag. Team only, no email, no stage change. */}
      <Dialog open={Boolean(flagFor)} onOpenChange={(o) => !o && setFlagFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Correction flag</DialogTitle>
            <DialogDescription>
              For the team only. The member sees nothing and the card stays where it is.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Date the state sent it back</Label>
              <Input type="date" value={flagReturned} onChange={(e) => setFlagReturned(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>Reason</Label>
              <Textarea value={flagReason} onChange={(e) => setFlagReason(e.target.value)} rows={3} />
            </div>
            <div className="space-y-1">
              <Label>Date we sent it back in</Label>
              <Input type="date" value={flagResubmitted} onChange={(e) => setFlagResubmitted(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFlagFor(null)}>Cancel</Button>
            <Button disabled={saving} onClick={() => flagFor && saveCorrection(flagFor)}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save flag
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Card>
        <CardHeader>
          <CardTitle className="text-base">Correction requests</CardTitle>
          <CardDescription>Members asking us to fix a field they cannot edit themselves.</CardDescription>
        </CardHeader>
        <CardContent>
          {corrections.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nothing waiting right now.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Member</TableHead>
                  <TableHead>Field</TableHead>
                  <TableHead>Now</TableHead>
                  <TableHead>Asked for</TableHead>
                  <TableHead>Note</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {corrections.map((c) => {
                  const m = rows.find((r) => r.id === c.member_record_id);
                  return (
                    <TableRow key={c.id}>
                      <TableCell>
                        <Link className="hover:underline" to={`/admin/members/${c.member_record_id}`}>
                          {m ? memberName(m) : "Member"}
                        </Link>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{c.field_key}</TableCell>
                      <TableCell className="text-xs">{c.current_value}</TableCell>
                      <TableCell className="text-xs">{c.requested_value}</TableCell>
                      <TableCell className="text-xs">{c.member_note}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="outline" onClick={() => void resolveCorrection(c)}>
                          Mark handled
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!target} onOpenChange={(open) => { if (!open) setTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {movingBackward ? "Move this member back" : "Move this member forward"}
            </DialogTitle>
            <DialogDescription>
              {target
                ? `${memberName(target.member)} goes from ${STAGE_LABEL[target.member.fulfillment_stage]} to ${STAGE_LABEL[target.toStage]}.`
                : ""}
            </DialogDescription>
          </DialogHeader>

          {mintedUrl ? (
            <div className="space-y-3">
              <p className="text-sm">This is the address behind the code in the packet.</p>
              <div className="flex items-center gap-2">
                <Input readOnly value={mintedUrl} className="font-mono text-xs" />
                <Button
                  size="icon"
                  variant="outline"
                  onClick={() => {
                    void navigator.clipboard.writeText(mintedUrl);
                    toast.success("Copied");
                  }}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <DialogFooter>
                <Button onClick={() => setTarget(null)}>Done</Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              {target?.toStage === "docs_sent" && (
                <div className="space-y-2">
                  <Label htmlFor="tracking">Tracking number</Label>
                  <Input
                    id="tracking"
                    value={tracking}
                    onChange={(e) => setTracking(e.target.value)}
                    placeholder="Paste the carrier tracking number"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="move-note">Note {movingBackward ? "(required)" : "(optional)"}</Label>
                <Textarea id="move-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3} />
              </div>

              {!movingBackward && (
                emailPreview ? (
                  <div className="rounded-md border bg-muted/30 p-3">
                    <div className="text-xs font-semibold uppercase text-muted-foreground">
                      This email goes out
                    </div>
                    <div className="mt-1 text-sm font-medium">{emailPreview.subject}</div>
                    <pre className="mt-2 whitespace-pre-wrap font-sans text-sm text-muted-foreground">
                      {emailPreview.body}
                    </pre>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No email wording is saved for this step yet, so nothing will be sent.
                  </p>
                )
              )}



              {movingBackward && (
                <div className="space-y-2">
                  <Label htmlFor="confirm">Type MOVE BACK to confirm</Label>
                  <Input id="confirm" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} />
                </div>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={() => setTarget(null)}>Cancel</Button>
                <Button onClick={() => void submitMove()} disabled={saving}>
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Save the move
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Print packet: approved papers plus team uploads for CP575E and CA(Reg). */}
      {packetFor && formations[packetFor.id] && (
        <PrintPacketDialog
          member={packetFor}
          formationId={formations[packetFor.id].id}
          onClose={() => setPacketFor(null)}
        />
      )}
    </div>
  );
}
